import { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { MessageSquare, ThumbsUp, AlertCircle, CheckCircle, Video } from 'lucide-react';

interface FeedbackItem {
  id: string;
  author: string;
  authorAvatar: string;
  comment: string;
  timestamp: string;
  type: 'comment' | 'approve' | 'request_change';
}

interface DraftElement {
  id: string;
  type: 'image' | 'video' | 'caption';
  content: string | string[];
  feedback: FeedbackItem[];
  status: 'pending' | 'approved' | 'needs_revision';
}

interface DraftPost {
  id: number;
  creatorName: string;
  creatorAvatar: string;
  creatorHandle: string;
  platform: 'Instagram' | 'TikTok' | 'YouTube' | 'Twitter';
  campaignName: string;
  brandName: string;
  elements: DraftElement[];
  lastEdited: string;
}

export function DraftsPage() {
  const [expandedFeedback, setExpandedFeedback] = useState<{ [key: string]: boolean }>({});
  const [feedbackText, setFeedbackText] = useState<{ [key: string]: string }>({});
  const [activeTab, setActiveTab] = useState<'all' | 'pending' | 'accepted' | 'declined'>('all');

  const drafts: DraftPost[] = [
    {
      id: 1,
      creatorName: 'Sarah Martinez',
      creatorAvatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100',
      creatorHandle: '@sarahstyle',
      platform: 'Instagram',
      campaignName: 'Summer Beauty Campaign',
      brandName: 'Glossier',
      lastEdited: '2 hours ago',
      elements: [
        {
          id: '1-img-1',
          type: 'image',
          content: 'https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=600',
          status: 'pending',
          feedback: [
            {
              id: 'fb-1',
              author: 'Brand Manager',
              authorAvatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50',
              comment: 'Love the natural lighting! Can we get a version with the product more centered?',
              timestamp: '1 hour ago',
              type: 'request_change'
            }
          ]
        },
        {
          id: '1-caption-1',
          type: 'caption',
          content: 'Glowing into summer with @glossier ✨ This Cloud Paint in Beam is giving me the perfect sun-kissed look without the sun damage. Swipe for my full routine! #GlossierPartner #SummerGlow',
          status: 'approved',
          feedback: [
            {
              id: 'fb-2',
              author: 'Marketing Lead',
              authorAvatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=50',
              comment: 'Perfect caption! Approved.',
              timestamp: '30 minutes ago',
              type: 'approve'
            }
          ]
        }
      ]
    },
    {
      id: 2,
      creatorName: 'Mike Johnson',
      creatorAvatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100',
      creatorHandle: '@mikefitness',
      platform: 'TikTok',
      campaignName: 'Nike Air Max Campaign',
      brandName: 'Nike',
      lastEdited: '4 hours ago',
      elements: [
        {
          id: '2-video-1',
          type: 'video',
          content: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=600',
          status: 'needs_revision',
          feedback: [
            {
              id: 'fb-3',
              author: 'Creative Director',
              authorAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=50',
              comment: 'Great energy! Can you add a close-up shot of the shoes at the 5-second mark?',
              timestamp: '3 hours ago',
              type: 'request_change'
            },
            {
              id: 'fb-4',
              author: 'Brand Manager',
              authorAvatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50',
              comment: 'Also, please make sure the Nike logo is visible in the first 3 seconds.',
              timestamp: '2 hours ago',
              type: 'request_change'
            }
          ]
        },
        {
          id: '2-caption-1',
          type: 'caption',
          content: 'These @nike Air Max are UNMATCHED 🔥 Perfect for my morning runs and they look fire too. Link in bio! #NikePartner #AirMax',
          status: 'pending',
          feedback: []
        }
      ]
    },
    {
      id: 3,
      creatorName: 'Emma Rodriguez',
      creatorAvatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100',
      creatorHandle: '@emmabeauty',
      platform: 'Instagram',
      campaignName: 'Fall Skincare Collection',
      brandName: 'CeraVe',
      lastEdited: '1 day ago',
      elements: [
        {
          id: '3-img-1',
          type: 'image',
          content: 'https://images.unsplash.com/photo-1556228578-0d85b1a4d571?w=600',
          status: 'approved',
          feedback: [
            {
              id: 'fb-5',
              author: 'Marketing Lead',
              authorAvatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=50',
              comment: 'Perfect product placement! Approved.',
              timestamp: '1 day ago',
              type: 'approve'
            }
          ]
        },
        {
          id: '3-caption-1',
          type: 'caption',
          content: 'My skin has never been happier 💙 @cerave Hydrating Facial Cleanser is a game-changer. Dermatologist-recommended and packed with ceramides! #CeraVePartner #Skincare',
          status: 'approved',
          feedback: [
            {
              id: 'fb-6',
              author: 'Brand Manager',
              authorAvatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50',
              comment: 'Great messaging! This aligns perfectly with our brand values.',
              timestamp: '1 day ago',
              type: 'approve'
            }
          ]
        }
      ]
    },
    {
      id: 4,
      creatorName: 'David Kim',
      creatorAvatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100',
      creatorHandle: '@davidkicks',
      platform: 'Instagram',
      campaignName: 'Adidas Streetwear Drop',
      brandName: 'Adidas',
      lastEdited: '2 days ago',
      elements: [
        {
          id: '4-img-1',
          type: 'image',
          content: 'https://images.unsplash.com/photo-1460353581641-37baddab0fa2?w=600',
          status: 'pending',
          feedback: []
        },
        {
          id: '4-caption-1',
          type: 'caption',
          content: 'Vintage vibes with the new @adidas Forum Lows 🏀 These are straight fire! Copped in 3 colors already. #AdidasPartner #ForumLow #Streetwear',
          status: 'needs_revision',
          feedback: [
            {
              id: 'fb-7',
              author: 'Brand Manager',
              authorAvatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50',
              comment: 'Can you mention the sustainability aspect of our new collection? It\'s a key message this season.',
              timestamp: '1 day ago',
              type: 'request_change'
            }
          ]
        }
      ]
    },
    {
      id: 5,
      creatorName: 'Sophia Chen',
      creatorAvatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100',
      creatorHandle: '@sophiamakeup',
      platform: 'TikTok',
      campaignName: 'Fenty Beauty Gloss',
      brandName: 'Fenty Beauty',
      lastEdited: '3 days ago',
      elements: [
        {
          id: '5-video-1',
          type: 'video',
          content: 'https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=600',
          status: 'approved',
          feedback: [
            {
              id: 'fb-8',
              author: 'Creative Director',
              authorAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=50',
              comment: 'The lighting and close-ups are perfect! Approved for publishing.',
              timestamp: '2 days ago',
              type: 'approve'
            }
          ]
        },
        {
          id: '5-caption-1',
          type: 'caption',
          content: 'That SHINE though 💋✨ @fentybeauty Gloss Bomb is the only gloss you need. No sticky feeling, just pure gloss goals! #FentyBeauty #GlossBomb',
          status: 'approved',
          feedback: []
        }
      ]
    }
  ];

  const getElementStatusColor = (status: DraftElement['status']) => {
    switch (status) {
      case 'approved':
        return 'bg-green-50 border-green-200';
      case 'needs_revision':
        return 'bg-orange-50 border-orange-200';
      case 'pending':
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  const getElementStatusIcon = (status: DraftElement['status']) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'needs_revision':
        return <AlertCircle className="w-4 h-4 text-orange-600" />;
      case 'pending':
      default:
        return <MessageSquare className="w-4 h-4 text-blue-600" />;
    }
  };

  const getElementStatusText = (status: DraftElement['status']) => {
    switch (status) {
      case 'approved':
        return 'Approved';
      case 'needs_revision':
        return 'Needs Revision';
      case 'pending':
      default:
        return 'Pending Review';
    }
  };

  const toggleFeedback = (elementId: string) => {
    setExpandedFeedback(prev => ({
      ...prev,
      [elementId]: !prev[elementId]
    }));
  };

  const handleAddFeedback = (elementId: string) => {
    const text = feedbackText[elementId];
    if (text && text.trim()) {
      // In a real app, this would submit to backend
      if (import.meta.env.DEV) console.log(`Adding feedback to ${elementId}:`, text);
      setFeedbackText(prev => ({ ...prev, [elementId]: '' }));
    }
  };

  const getPlatformColor = (platform: string) => {
    switch (platform) {
      case 'Instagram':
        return 'bg-gradient-to-r from-purple-500 to-pink-500 text-white';
      case 'TikTok':
        return 'bg-black text-white';
      case 'YouTube':
        return 'bg-red-600 text-white';
      case 'Twitter':
        return 'bg-blue-400 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <div className="max-w-7xl mx-auto p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-gray-900 mb-2">Drafts</h1>
          <p className="text-gray-600">
            Review and provide feedback on creator content
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <div className="flex gap-8">
              <button
                onClick={() => setActiveTab('all')}
                className={`pb-3 px-1 border-b-2 transition-colors ${
                  activeTab === 'all'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="font-medium">All</span>
                <span className="ml-2 px-2 py-0.5 rounded-full bg-gray-100 text-xs text-gray-600">
                  {drafts.length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('pending')}
                className={`pb-3 px-1 border-b-2 transition-colors ${
                  activeTab === 'pending'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="font-medium">Pending</span>
                <span className="ml-2 px-2 py-0.5 rounded-full bg-yellow-100 text-xs text-yellow-800">
                  {drafts.filter(d => d.elements.some(e => e.status === 'pending')).length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('accepted')}
                className={`pb-3 px-1 border-b-2 transition-colors ${
                  activeTab === 'accepted'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="font-medium">Accepted</span>
                <span className="ml-2 px-2 py-0.5 rounded-full bg-green-100 text-xs text-green-800">
                  {drafts.filter(d => d.elements.every(e => e.status === 'approved')).length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('declined')}
                className={`pb-3 px-1 border-b-2 transition-colors ${
                  activeTab === 'declined'
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="font-medium">Declined</span>
                <span className="ml-2 px-2 py-0.5 rounded-full bg-red-100 text-xs text-red-800">
                  0
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Drafts Feed */}
        <div className="space-y-6">
          {drafts.map((draft) => (
            <Card key={draft.id} className="bg-white border border-gray-200 overflow-hidden">
              <div className="p-6">
                {/* Creator Header */}
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                  <img
                    src={draft.creatorAvatar}
                    alt={draft.creatorName}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-gray-900 font-medium">{draft.creatorName}</p>
                      <span className="text-gray-500 text-sm">{draft.creatorHandle}</span>
                      <Badge className={`${getPlatformColor(draft.platform)} border-0 text-xs`}>
                        {draft.platform}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600">
                      {draft.brandName} • {draft.campaignName}
                    </p>
                  </div>
                  <span className="text-sm text-gray-500">{draft.lastEdited}</span>
                </div>

                {/* Two Column Layout: Content Left, Caption Right */}
                <div className="grid grid-cols-2 gap-6">
                  {/* Left Column: Image/Video */}
                  <div className="space-y-4">
                    {draft.elements
                      .filter(el => el.type === 'image' || el.type === 'video')
                      .map((element) => (
                        <div key={element.id}>
                          {/* Element Content */}
                          <div className={`rounded-lg border-2 overflow-hidden ${getElementStatusColor(element.status)}`}>
                            {element.type === 'image' && (
                              <div className="relative">
                                <img
                                  src={element.content as string}
                                  alt="Draft content"
                                  className="w-full h-auto"
                                />
                                <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-2 shadow-sm">
                                  {getElementStatusIcon(element.status)}
                                  <span className="text-sm font-medium text-gray-700">
                                    {getElementStatusText(element.status)}
                                  </span>
                                </div>
                              </div>
                            )}

                            {element.type === 'video' && (
                              <div className="relative">
                                <img
                                  src={element.content as string}
                                  alt="Video thumbnail"
                                  className="w-full h-auto"
                                />
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="w-16 h-16 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg">
                                    <Video className="w-8 h-8 text-gray-900" />
                                  </div>
                                </div>
                                <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-2 shadow-sm">
                                  {getElementStatusIcon(element.status)}
                                  <span className="text-sm font-medium text-gray-700">
                                    {getElementStatusText(element.status)}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Feedback Section */}
                          <div className="mt-3 bg-gray-50 rounded-lg p-4">
                            {/* Feedback Toggle Button */}
                            <button
                              onClick={() => toggleFeedback(element.id)}
                              className="w-full flex items-center justify-between text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <MessageSquare className="w-4 h-4" />
                                <span>
                                  {element.feedback.length > 0
                                    ? `${element.feedback.length} ${element.feedback.length === 1 ? 'comment' : 'comments'}`
                                    : 'Add feedback'}
                                </span>
                              </div>
                              <span className="text-gray-400">
                                {expandedFeedback[element.id] ? '−' : '+'}
                              </span>
                            </button>

                            {/* Expanded Feedback */}
                            {expandedFeedback[element.id] && (
                              <div className="mt-4 space-y-3">
                                {/* Existing Feedback */}
                                {element.feedback.map((fb) => (
                                  <div key={fb.id} className="bg-white rounded-lg p-3 border border-gray-200">
                                    <div className="flex items-start gap-3">
                                      <img
                                        src={fb.authorAvatar}
                                        alt={fb.author}
                                        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="text-sm font-medium text-gray-900">{fb.author}</span>
                                          <span className="text-xs text-gray-500">{fb.timestamp}</span>
                                          {fb.type === 'approve' && (
                                            <Badge className="bg-green-100 text-green-800 border-0 text-xs">
                                              Approved
                                            </Badge>
                                          )}
                                          {fb.type === 'request_change' && (
                                            <Badge className="bg-orange-100 text-orange-800 border-0 text-xs">
                                              Change Requested
                                            </Badge>
                                          )}
                                        </div>
                                        <p className="text-sm text-gray-700">{fb.comment}</p>
                                      </div>
                                    </div>
                                  </div>
                                ))}

                                {/* Add Feedback Form */}
                                <div className="bg-white rounded-lg p-3 border border-gray-200">
                                  <Textarea
                                    placeholder="Add your feedback..."
                                    value={feedbackText[element.id] || ''}
                                    onChange={(e) => setFeedbackText(prev => ({
                                      ...prev,
                                      [element.id]: e.target.value
                                    }))}
                                    className="mb-3 min-h-[80px] text-sm resize-none"
                                  />
                                  <div className="flex items-center gap-2">
                                    <Button
                                      size="sm"
                                      onClick={() => handleAddFeedback(element.id)}
                                      className="bg-gray-900 text-white hover:bg-gray-800"
                                    >
                                      <MessageSquare className="w-4 h-4 mr-2" />
                                      Comment
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleAddFeedback(element.id)}
                                      className="text-green-600 border-green-200 hover:bg-green-50"
                                    >
                                      <ThumbsUp className="w-4 h-4 mr-2" />
                                      Approve
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleAddFeedback(element.id)}
                                      className="text-orange-600 border-orange-200 hover:bg-orange-50"
                                    >
                                      <AlertCircle className="w-4 h-4 mr-2" />
                                      Request Changes
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>

                  {/* Right Column: Caption */}
                  <div className="space-y-4">
                    {draft.elements
                      .filter(el => el.type === 'caption')
                      .map((element) => (
                        <div key={element.id}>
                          {/* Element Content */}
                          <div className={`rounded-lg border-2 overflow-hidden ${getElementStatusColor(element.status)}`}>
                            <div className="p-4 bg-white">
                              <div className="flex items-start justify-between mb-2">
                                <p className="text-sm text-gray-900 flex-1">{element.content as string}</p>
                                <div className="ml-4 flex items-center gap-2 px-3 py-1 bg-gray-50 rounded-full flex-shrink-0">
                                  {getElementStatusIcon(element.status)}
                                  <span className="text-sm font-medium text-gray-700">
                                    {getElementStatusText(element.status)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Feedback Section */}
                          <div className="mt-3 bg-gray-50 rounded-lg p-4">
                            {/* Feedback Toggle Button */}
                            <button
                              onClick={() => toggleFeedback(element.id)}
                              className="w-full flex items-center justify-between text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <MessageSquare className="w-4 h-4" />
                                <span>
                                  {element.feedback.length > 0
                                    ? `${element.feedback.length} ${element.feedback.length === 1 ? 'comment' : 'comments'}`
                                    : 'Add feedback'}
                                </span>
                              </div>
                              <span className="text-gray-400">
                                {expandedFeedback[element.id] ? '−' : '+'}
                              </span>
                            </button>

                            {/* Expanded Feedback */}
                            {expandedFeedback[element.id] && (
                              <div className="mt-4 space-y-3">
                                {/* Existing Feedback */}
                                {element.feedback.map((fb) => (
                                  <div key={fb.id} className="bg-white rounded-lg p-3 border border-gray-200">
                                    <div className="flex items-start gap-3">
                                      <img
                                        src={fb.authorAvatar}
                                        alt={fb.author}
                                        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="text-sm font-medium text-gray-900">{fb.author}</span>
                                          <span className="text-xs text-gray-500">{fb.timestamp}</span>
                                          {fb.type === 'approve' && (
                                            <Badge className="bg-green-100 text-green-800 border-0 text-xs">
                                              Approved
                                            </Badge>
                                          )}
                                          {fb.type === 'request_change' && (
                                            <Badge className="bg-orange-100 text-orange-800 border-0 text-xs">
                                              Change Requested
                                            </Badge>
                                          )}
                                        </div>
                                        <p className="text-sm text-gray-700">{fb.comment}</p>
                                      </div>
                                    </div>
                                  </div>
                                ))}

                                {/* Add Feedback Form */}
                                <div className="bg-white rounded-lg p-3 border border-gray-200">
                                  <Textarea
                                    placeholder="Add your feedback..."
                                    value={feedbackText[element.id] || ''}
                                    onChange={(e) => setFeedbackText(prev => ({
                                      ...prev,
                                      [element.id]: e.target.value
                                    }))}
                                    className="mb-3 min-h-[80px] text-sm resize-none"
                                  />
                                  <div className="flex flex-col gap-2">
                                    <Button
                                      size="sm"
                                      onClick={() => handleAddFeedback(element.id)}
                                      className="bg-gray-900 text-white hover:bg-gray-800 w-full"
                                    >
                                      <MessageSquare className="w-4 h-4 mr-2" />
                                      Comment
                                    </Button>
                                    <div className="grid grid-cols-2 gap-2">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleAddFeedback(element.id)}
                                        className="text-green-600 border-green-200 hover:bg-green-50"
                                      >
                                        <ThumbsUp className="w-4 h-4 mr-2" />
                                        Approve
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleAddFeedback(element.id)}
                                        className="text-orange-600 border-orange-200 hover:bg-orange-50"
                                      >
                                        <AlertCircle className="w-4 h-4 mr-2" />
                                        Request
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}